import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import Dropdown from './Dropdown/Dropdown';
import { buildOptionSearchIndex, checkIfValueIncludes, checkIfValueStartsWith, getPrimaryLabel, setOptionLabel } from './searchHelper';

import { useAutocompleteFocusBehavior } from 'hooks/transaction/form/useAutocompleteFocusBehavior';
import PropTypes from 'prop-types';
import styles from './AIQAutocomplete.module.css';

// Cap how many matches we render/navigate at once. On desks with very large
// lookup lists (e.g. driver names) rendering thousands of dropdown rows per
// keystroke stutters the UI. Matches are already ranked (startsWith before
// includes), so the best results are kept; users narrow further by typing.
//
// This is the main tuning knob for dropdown cost. Keystrokes themselves no
// longer scale with it (rows are memoized against the applied filter text), but
// the deferred re-render when results change still does. Measured in jsdom on a
// realistic 1000-row personnel lookup, that re-render costs roughly 13ms at 25,
// 25ms at 50 and 45ms at 100; a real browser runs about 3x those numbers. The
// dropdown is 200px tall and shows ~4 alert rows at a time, so 25 is already
// several screens of scrolling before the "keep typing to narrow" hint applies.
export const MAX_RENDERED_OPTIONS = 50;

// How long we wait after the last keystroke before (re)filtering a potentially
// large option list. Kept as a named constant (like MAX_RENDERED_OPTIONS) so the
// value isn't a magic number scattered inline.
export const FILTER_DEBOUNCE_MS = 120;

/**
 * Controlled: the input always shows `value`. With `freeSolo`, the consumer MUST store the typed text from
 * `onInputChange` on every keystroke (as Select does through fieldControllers.inputValueChangeHandler) -
 * otherwise the text is wiped as soon as `value` changes to empty.
 */
function AIQAutocomplete({
    renderInput,
    options,
    getOptionLabel,
    onInputChange = () => {},
    onChange = () => {},
    onBlur = () => {},
    value,
    freeSolo,
    containerClasses,
    noOptionsText = 'No options...',
    isOptionsLoadingInProgress,
    optionsLoadingText = 'Loading options...',
    referenceExpr,
    initialReferenceValue,
    onSelect,
    isInventoryProperty,
    fieldName,
    isClientEquipmentEnabled,
    aiSuggestions,
    ocrGroups,
    valueExpr,
}) {
    const getInputValue = useCallback(
        (value) => {
            return getOptionLabel ? setOptionLabel(value, getOptionLabel) : (value ?? '');
        },
        [getOptionLabel]
    );

    const getOption = useCallback(
        (optionLabel, referenceValue) => {
            if (referenceValue && referenceValue >= 0) return options.find((option) => option[referenceExpr] === referenceValue);
            if (getOptionLabel) return options.find((option) => getOptionLabel(option) === optionLabel);
            return optionLabel;
        },
        [getOptionLabel, referenceExpr, options]
    );
    const [filteredOptions, setFilteredOptions] = useState(() => options.slice(0, MAX_RENDERED_OPTIONS));
    const [isTruncated, setIsTruncated] = useState(false);
    const [inputValue, setInputValue] = useState(getInputValue(value));
    // The input text that `filteredOptions` was produced from. The rows are given
    // THIS rather than the live `inputValue`, so that a keystroke - which updates
    // `inputValue` synchronously to keep typing responsive - does not change any
    // row's props and therefore re-render (and re-highlight) every mounted row
    // before the debounced filter has even run. Rows update once, when results do.
    const [appliedInputValue, setAppliedInputValue] = useState(() => getInputValue(value));
    const [referenceValue, setReferenceValue] = useState(initialReferenceValue);
    const [dropdownOpened, setDropdownOpened] = useState(false);
    const containerRef = useRef(null);
    const filterDebounceRef = useRef(null);
    // Holds the input text of a not-yet-applied (debounced) filter so we can flush
    // it synchronously when the user starts navigating with the keyboard.
    const pendingFilterTextRef = useRef(null);
    // Latest committed input text, for the applyOptions callers (focus/mousedown)
    // that are invoked from a stable callback and cannot close over `inputValue`.
    const latestInputValueRef = useRef(inputValue);

    useEffect(() => {
        latestInputValueRef.current = inputValue;
    }, [inputValue]);

    // Single place where the rendered list is set. EVERY path that populates the
    // dropdown must go through here, not just the filter path: opening the
    // dropdown, clearing the input and blurring all used to assign the full
    // option array, so a large lookup (e.g. ~1000 driver names) mounted every row
    // and the next keystroke re-rendered all of them before the debounced filter
    // had a chance to trim the list.
    // `text` is the input text these results correspond to; it is what the rows
    // highlight against. Defaults to the latest committed input text for callers
    // (focus, mousedown) that are just re-showing the unfiltered list.
    const applyOptions = useCallback((list, text) => {
        const truncated = list.length > MAX_RENDERED_OPTIONS;
        setFilteredOptions(truncated ? list.slice(0, MAX_RENDERED_OPTIONS) : list);
        setIsTruncated(truncated);
        setAppliedInputValue(text ?? latestInputValueRef.current);
    }, []);

    // Precompute, once per option-list change, both the lowercased searchable
    // strings AND the lowercased primary label for every option, so per-keystroke
    // filtering is cheap (no deep re-walking/re-lowercasing of each option and no
    // re-calling getOptionLabel per option per keystroke). Large lists (e.g.
    // driver name lookups on some desks) used to make typing freeze the field.
    const { searchIndex, primaryIndex } = useMemo(() => buildOptionSearchIndex(options, getOptionLabel), [options, getOptionLabel]);

    // Filtering runs on a short debounce so rapid typing / backspacing doesn't
    // trigger a full scan of a large list on every keystroke. The input value
    // itself is updated immediately (in inputChangeHandler) so typing stays
    // responsive; only the dropdown results catch up shortly after.
    const computeFilteredOptions = useCallback(
        (inputText) => {
            const primaryStartsWith = [];
            const primaryIncludes = [];
            const secondaryStartsWith = [];
            const secondaryIncludes = [];
            const inputTextStandardized = inputText.trim().toLowerCase();

            options.forEach((option) => {
                // Precomputed (lowercased) primary label; avoids re-calling
                // getOptionLabel + toString + toLowerCase per option per keystroke.
                const primaryStr = getPrimaryLabel(option, primaryIndex, getOptionLabel);

                if (primaryStr && primaryStr.startsWith(inputTextStandardized)) {
                    primaryStartsWith.push(option);
                } else if (primaryStr && primaryStr.includes(inputTextStandardized)) {
                    primaryIncludes.push(option);
                } else if (checkIfValueStartsWith(option, inputTextStandardized, searchIndex)) {
                    secondaryStartsWith.push(option);
                } else if (checkIfValueIncludes(option, inputTextStandardized, searchIndex)) {
                    secondaryIncludes.push(option);
                }
            });

            // Ranked best-first. Capping happens in applyOptions so there is a
            // single place that decides how many rows are rendered.
            return [...primaryStartsWith, ...primaryIncludes, ...secondaryStartsWith, ...secondaryIncludes];
        },
        [options, getOptionLabel, searchIndex, primaryIndex]
    );

    // Applies any pending (debounced) filter immediately. Called when the user
    // starts navigating with the keyboard (Arrow/Enter/Tab) before the debounce
    // has fired, so keyboard navigation always runs against up-to-date results
    // instead of the previous (stale) list.
    const flushPendingFilter = useCallback(() => {
        if (!filterDebounceRef.current) return;
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = null;

        const pendingText = pendingFilterTextRef.current;
        pendingFilterTextRef.current = null;
        if (pendingText == null) return;

        applyOptions(computeFilteredOptions(pendingText), pendingText);
    }, [computeFilteredOptions, applyOptions]);

    useEffect(() => {
        return () => {
            if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
        };
    }, []);

    // Flush a pending debounced filter as soon as the user starts keyboard
    // navigation, before useDropdownBehavior's (document-level) keydown handler
    // acts on the options. A capture-phase listener on the container guarantees
    // this runs first. Without it, a fast user could Arrow/Enter within the
    // debounce window and navigate/select stale results.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const NAV_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Tab']);
        const handleKeyDownCapture = (e) => {
            if (NAV_KEYS.has(e.key)) {
                flushPendingFilter();
            }
        };

        container.addEventListener('keydown', handleKeyDownCapture, true);
        return () => container.removeEventListener('keydown', handleKeyDownCapture, true);
    }, [flushPendingFilter]);

    const { focusHandler, containerMouseDownHandler } = useAutocompleteFocusBehavior({
        dropdownOpened,
        setDropdownOpened,
        // Capped setter: opening the dropdown must not mount the entire list.
        setFilteredOptions: applyOptions,
        options,
        fieldName,
    });

    // The input always shows the stored value (see the free-solo contract above): an empty value means
    // the field really is empty, so old text left there would hide that and let CTRL+Enter fill it.
    useEffect(() => {
        setInputValue(getInputValue(value));
    }, [value, getInputValue]);

    function inputBlurHandler(e) {
        const inputText = e.target.value;

        // Cancel any pending debounced filter so it can't fire after blur and
        // repopulate the (now reset) dropdown options.
        if (filterDebounceRef.current) {
            clearTimeout(filterDebounceRef.current);
            filterDebounceRef.current = null;
        }

        // Clear the hint up front. The paths below that repopulate the list go
        // through applyOptions, which recomputes it; the early-return path does
        // not, and the dropdown is closing anyway.
        setIsTruncated(false);

        if (inputValue === '' && inputText !== '' && getOptionLabel) {
            const option = getOption(inputText, referenceValue);
            if (option) {
                onChange(e, option);
                onBlur(e);
                return;
            }
        }

        if (inputValue === '') {
            if (value) {
                onChange(e, null);
            }
            applyOptions(options);
            onBlur(e);
            return;
        }

        const selectedOption = getOption(inputValue, referenceValue);

        if (selectedOption) {
            onChange(e, selectedOption);
            applyOptions(options);
        } else if (freeSolo) {
            applyOptions(options);
        } else {
            setInputValue(getInputValue(value));
            !value && applyOptions(options);
        }

        if (typeof value === 'object') {
            applyOptions(options);
        }

        onBlur(e);
    }

    function inputChangeHandler(e) {
        const inputText = e.target.value;

        // Update the input value immediately so typing/backspacing stays snappy.
        setInputValue(inputText);
        setReferenceValue(-1);
        onInputChange(e, inputText);

        if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);

        if (inputText === '') {
            pendingFilterTextRef.current = null;
            applyOptions(options, '');
            return;
        }

        // Remember the latest text so a keyboard-triggered flush can apply it
        // synchronously if the user navigates before the debounce fires.
        pendingFilterTextRef.current = inputText;

        // Debounce the (potentially expensive) filtering of a large option list.
        filterDebounceRef.current = setTimeout(() => {
            filterDebounceRef.current = null;
            pendingFilterTextRef.current = null;
            applyOptions(computeFilteredOptions(inputText), inputText);
        }, FILTER_DEBOUNCE_MS);
    }

    useEffect(() => {
        if (!inputValue) {
            applyOptions(options, inputValue);
        }
        // options.length is redundant here: any change to the options array
        // already changes the `options` reference, which re-runs this effect.
    }, [options, inputValue, applyOptions]);

    const hasAlerts = useMemo(() => !!options.find((option) => option.alerts), [options]);

    const handleAiSuggestionSelect = useCallback(
        (suggestionValue) => {
            let matchedOption = null;
            if (valueExpr) {
                matchedOption = options.find((opt) => opt[valueExpr] === suggestionValue);
            }
            if (!matchedOption && getOptionLabel) {
                matchedOption = options.find((opt) => getOptionLabel(opt) === suggestionValue);
            }

            if (matchedOption) {
                setInputValue(getInputValue(matchedOption));
                onChange(null, matchedOption);
            } else {
                const displayValue = typeof suggestionValue === 'object' ? getInputValue(suggestionValue) : String(suggestionValue ?? '');
                setInputValue(displayValue);
                onInputChange({ target: { value: displayValue } }, displayValue);
            }
            setDropdownOpened(false);
        },
        [options, valueExpr, getOptionLabel, getInputValue, onChange, onInputChange, setInputValue]
    );

    const getCurrentInputFieldId = useCallback(() => {
        const inputId = containerRef.current?.querySelector('input')?.id;
        return inputId ? +inputId : null;
    }, []);

    return (
        <div
            ref={containerRef}
            // BEAST-13036. The option list renders INSIDE this container, so a focus move into the
            // dropdown still resolves to the same field key - which is what keeps opening a
            // composite input's dropdown from ending the field focus period.
            data-field-name={fieldName}
            className={`${styles['autocomplete-container']}${containerClasses ? ` ${containerClasses}` : ''}`}
            onMouseDown={containerMouseDownHandler}
        >
            {renderInput({
                option: getOption(inputValue, referenceValue),
                value: inputValue,
                onChange: inputChangeHandler,
                onBlur: inputBlurHandler,
                onFocus: focusHandler,
                dropdownArrow: true,
            })}
            <Dropdown
                {...{
                    isOptionsLoadingInProgress,
                    optionsLoadingText,
                    dropdownOpened,
                    filteredOptions,
                    isTruncated,
                    maxRenderedOptions: MAX_RENDERED_OPTIONS,
                    noOptionsText,
                    onChange,
                    setDropdownOpened,
                    setInputValue,
                    setReferenceValue,
                    freeSolo,
                    getInputValue,
                    value,
                    // Deliberately the applied (results-synced) text, not the live
                    // `inputValue` - see the appliedInputValue declaration above.
                    inputValue: appliedInputValue,
                    hasAlerts,
                    isInventoryProperty,
                    fieldName,
                    referenceExpr,
                    onSelect,
                    isClientEquipmentEnabled,
                    aiSuggestions,
                    ocrGroups,
                    onAiSuggestionSelect: handleAiSuggestionSelect,
                    anchorRef: containerRef,
                    getCurrentInputFieldId,
                }}
            />
        </div>
    );
}

AIQAutocomplete.propTypes = {
    renderInput: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    getOptionLabel: PropTypes.func,
    // Required in practice with `freeSolo`: must store the typed text on every call.
    onInputChange: PropTypes.func,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.any,
    freeSolo: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    containerClasses: PropTypes.string,
    noOptionsText: PropTypes.string,
    isOptionsLoadingInProgress: PropTypes.bool,
    optionsLoadingText: PropTypes.string,
    referenceExpr: PropTypes.string,
    initialReferenceValue: PropTypes.any,
    onSelect: PropTypes.func,
    isInventoryProperty: PropTypes.bool,
    fieldName: PropTypes.string,
    isClientEquipmentEnabled: PropTypes.bool,
    aiSuggestions: PropTypes.array,
    ocrGroups: PropTypes.array,
    valueExpr: PropTypes.string,
};

export default AIQAutocomplete;
